import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  UserProfileResponse,
  FreezeAccountResponse,
  OwnUnfreezeRequestResponse,
  UnfreezeRequestResponse,
  EmergencyStopResponse,
  UserRolesResponse,
} from '../models/dashboard.model';
import { ApiResponse } from '@shared/models/api-response.models';
import { environment } from '../../../environments/environment';

/**
 * All HTTP calls in this service return the standard
 * `ApiResponse<T>` envelope. Callers must read inner payloads via
 * `res.data` and may read `res.message` for the localized server message
 * (typically toasted automatically by `apiResponseInterceptor`).
 */
@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private apiUrl = environment.userManagementApiUrl;
  private policyManagementApiUrl = environment.policyManagementApiUrl;

  constructor(private http: HttpClient) {}

  getProfile(): Observable<ApiResponse<UserProfileResponse>> {
    return this.http.get<ApiResponse<UserProfileResponse>>(
      `${this.apiUrl}/api/users/profile`
    );
  }

  getRoles(): Observable<ApiResponse<UserRolesResponse>> {
    return this.http.get<ApiResponse<UserRolesResponse>>(
      `${this.apiUrl}/api/roles?action=all`
    );
  }

  freezeAccount(): Observable<ApiResponse<FreezeAccountResponse>> {
    return this.http.post<ApiResponse<FreezeAccountResponse>>(
      `${this.apiUrl}/api/user/v1/freeze`,
      {}
    );
  }

  /** The caller's own pending unfreeze request (null when there is none). */
  getOwnUnfreezeRequest(): Observable<ApiResponse<OwnUnfreezeRequestResponse>> {
    return this.http.get<ApiResponse<OwnUnfreezeRequestResponse>>(
      `${this.apiUrl}/api/user/v1/unfreeze-request`
    );
  }

  /** A frozen user asks the account owners to unfreeze them. */
  requestOwnUnfreeze(): Observable<ApiResponse<UnfreezeRequestResponse>> {
    return this.http.post<ApiResponse<UnfreezeRequestResponse>>(
      `${this.apiUrl}/api/user/v1/unfreeze-request`,
      {}
    );
  }

  emergencyStopAccount(
    reason: string
  ): Observable<ApiResponse<EmergencyStopResponse>> {
    return this.http.post<ApiResponse<EmergencyStopResponse>>(
      `${this.apiUrl}/api/account/v1/freeze`,
      { reason }
    );
  }

  killSwitch(
    reason: string,
    enablePanicMode: boolean
  ): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(
      `${this.policyManagementApiUrl}/api/v1/approval-requests/kill-switch`,
      { reason, enablePanicMode }
    );
  }
}
